import React, { FunctionComponent } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { Grid } from 'semantic-ui-react';
import withStyles from 'react-jss';
import classnames from 'classnames';
import { isEmpty, reject, propOr } from 'ramda';

import { ClassProp } from 'helpers/types';
import { theme } from 'config';

import ColorPicker from 'components/FormComponents/ColorPicker';
import UploadWidget from 'components/FormComponents/UploadWidget';
import FormTextArea from 'components/FormComponents/FormTextArea';
import {
  AssetBundle,
  AssetName,
} from 'components/FormComponents/UploadWidget/UploadWidget.helpers';

import {
  Merchant,
  AssetsDefaultBranding,
  BrandTypename,
  BundleImage,
  BrandAssetComponent,
  buildBrandAssetImage,
  buildBrandAssetColor,
  buildBundleComponent,
  checkForPrevious,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantBrandAssets/MerchantBrandAssets.helpers';
import styles from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantBrandAssets/MerchantBrandAssets.styles';

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  classes: Styles;
  brandAssets: AssetsDefaultBranding;
  merchant: Merchant;
  upsertBrandAssets: Function;
  isSubmitting: boolean;
};

const DefaultAssetsUpdate: FunctionComponent<Props> = ({
  classes,
  merchant,
  brandAssets,
  upsertBrandAssets,
  isSubmitting,
}) => {
  const { merchantName, brandId } = merchant;
  const {
    icon,
    banner,
    featuredBanner,
    featuredBannerHeadline,
    card,
    primaryColor,
    secondaryColor,
  } = brandAssets;

  const history = useHistory();
  const methods = useForm({
    mode: 'onBlur',
    defaultValues: {
      featuredBannerHeadline: propOr('', 'value', featuredBannerHeadline),
    },
  });
  const { register, handleSubmit } = methods;

  const onSubmit = (assetDetails: any) => {
    const {
      icon: iconPrev,
      banner: bannerPrev,
      card: cardPrev,
      featuredBanner: featuredBannerPrev,
    } = brandAssets;
    const {
      logo,
      banner,
      featuredBanner,
      featuredBannerHeadline,
      card,
      primaryColor,
      secondaryColor,
    } = assetDetails;

    const iconCurr = buildBrandAssetImage(
      logo,
      'image',
      BrandAssetComponent.icon,
      BrandTypename.BrandAssetIcon,
    );
    const cardCurr = buildBrandAssetImage(
      card,
      'image',
      BrandAssetComponent.cardImage,
      BrandTypename.BrandAssetCard,
    );
    const bannerCurr = buildBrandAssetImage(
      banner,
      'image',
      BrandAssetComponent.banner,
      BrandTypename.BrandAssetBanner,
    );
    const featuredBannerCurr = buildBrandAssetImage(
      featuredBanner,
      'image',
      BrandAssetComponent.featuredBanner,
      BrandTypename.BrandAssetFeaturedBanner,
    );
    const featuredBannerHeadlineCurr = buildBundleComponent(
      featuredBannerHeadline,
      'template',
      BrandAssetComponent.featuredBannerHeadline,
      BrandTypename.BrandAssetFeaturedBannerHeadline,
    );
    const primary = buildBrandAssetColor(
      primaryColor,
      'color',
      BrandAssetComponent.primaryColor,
      BrandTypename.BrandAssetPrimaryColor,
    );
    const secondary = buildBrandAssetColor(
      secondaryColor,
      'color',
      BrandAssetComponent.secondaryColor,
      BrandTypename.BrandAssetSecondaryColor,
    );
    const icon = checkForPrevious(iconPrev, iconCurr as BundleImage);
    const cardImg = checkForPrevious(cardPrev, cardCurr as BundleImage);
    const bannerImg = checkForPrevious(bannerPrev, bannerCurr as BundleImage);
    const featuredBannerImg = checkForPrevious(
      featuredBannerPrev,
      featuredBannerCurr as BundleImage,
    );

    const assets = reject(isEmpty, [
      icon,
      cardImg,
      bannerImg,
      featuredBannerImg,
      featuredBannerHeadlineCurr,
      primary,
      secondary,
    ]);

    upsertBrandAssets(assets);
  };

  return (
    <Grid className={classes.centerColumn}>
      <Grid.Column className={classes.columnWide}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <input
              type="hidden"
              name="brandId"
              value={brandId}
              ref={register}
            />
            <Grid className={classes.formGrid} columns={2}>
              <Grid.Row>
                <Grid.Column>
                  <UploadWidget
                    name="logo"
                    label="Logo"
                    defaultImage={propOr('', 'path', icon)}
                    merchantName={merchantName}
                    assetBundle={AssetBundle.DEFAULT_BRANDING}
                    assetName={AssetName.LOGO}
                    size="small"
                    tabIndex={3}
                    register={register()}
                  />
                </Grid.Column>
                <Grid.Column>
                  <UploadWidget
                    name="card"
                    label="Merchandising Card"
                    defaultImage={propOr('', 'path', card)}
                    merchantName={merchantName}
                    assetBundle={AssetBundle.DEFAULT_BRANDING}
                    assetName={AssetName.CARD}
                    tabIndex={4}
                    register={register()}
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column>
                  <UploadWidget
                    name="banner"
                    label="Detail Header"
                    defaultImage={propOr('', 'path', banner)}
                    merchantName={merchantName}
                    assetBundle={AssetBundle.DEFAULT_BRANDING}
                    assetName={AssetName.BANNER}
                    tabIndex={5}
                    register={register()}
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column>
                  <UploadWidget
                    name="featuredBanner"
                    label="Featured Banner"
                    defaultImage={propOr('', 'path', featuredBanner)}
                    merchantName={merchantName}
                    assetBundle={AssetBundle.DEFAULT_BRANDING}
                    assetName={AssetName.FEATUREDBANNER}
                    assetFolder="featured/dosh"
                    filePrefix="Featured_Banner_Header_"
                    tabIndex={6}
                    register={register()}
                  />
                </Grid.Column>
                <Grid.Column>
                  <FormTextArea
                    name="featuredBannerHeadline"
                    type="text"
                    placeholder="Enter featured banner headline"
                    label="Featured Banner Headline"
                    register={register()}
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row columns={4}>
                <Grid.Column>
                  <ColorPicker
                    type="text"
                    placeholder="Primary Color"
                    label="Primary Color"
                    register={register()}
                    name="primaryColor"
                    defaultValue={propOr(theme.violet, 'value', primaryColor)}
                    image={propOr('', 'path', icon)}
                  />
                </Grid.Column>
                <Grid.Column>
                  <ColorPicker
                    type="text"
                    placeholder="Secondary Color"
                    label="Secondary Color"
                    register={register()}
                    name="secondaryColor"
                    defaultValue={propOr(
                      theme.softSeafoamGreen,
                      'value',
                      secondaryColor,
                    )}
                    image={propOr('', 'path', icon)}
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
            <Grid>
              <Grid.Row className={classes.formRow}>
                <button
                  className={classnames(
                    classes.button,
                    classes.buttonSecondary,
                    classes.mediumBtn,
                  )}
                  onClick={history.goBack}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={classnames(classes.button, classes.mediumBtn)}
                >
                  {isSubmitting ? '...Submitting' : 'Save'}
                </button>
              </Grid.Row>
            </Grid>
          </form>
        </FormProvider>
      </Grid.Column>
    </Grid>
  );
};

export default withStyles(styles)(DefaultAssetsUpdate);
